import { skyEncounterBeat } from './88-sky-encounter-beat.mjs';
import { skyClearBossGround } from './86-sky-world-scale.mjs';
import { skyBossTravel } from './85-sky-boss-designs.mjs';

/** Original campaign art and encounter schedule. References inform pacing only;
 * names, palettes and guardian identities belong to Sky Rider. Speeds are m/s.
 * Frozen descriptors are shared by policy and presentation without renderer state. */
export const SKY_CONTENT_VERSION = 'sky-rider-17';

const palettes = {
  forest: ['#17344d', '#a6bda5', '#315b49', '#71f5cf'],
  ruins: ['#25465a', '#ddbb8c', '#756046', '#fbc777'],
  fungi: ['#30234d', '#ac88b2', '#50425e', '#e69cfa'],
  tunnel: ['#182b42', '#7896a6', '#354357', '#8ddcfa'],
  ice: ['#25446c', '#bbdbe0', '#769ba8', '#a4ffef'],
  void: ['#251d42', '#8a80a5', '#454154', '#d5b3ff'],
  citadel: ['#17283e', '#bc9c89', '#55505a', '#ffd493'],
};
const stage = (name, biome, bossName, bossForm, bossProfile, index, kind = 'combat') => {
  const [sky, fog, ground, accent] = palettes[biome];
  return Object.freeze({
    id: `legacy-sector-${String(index).padStart(2, '0')}`,
    retainedLegacy: true,
    ...(index === 0 ? { artVariant: 'first-dragon' } : {}),
    name,
    biome,
    sky,
    fog,
    ground,
    accent,
    bossName,
    bossForm,
    bossProfile,
    bossDesign: kind === 'bonus' || kind === 'rush' ? null : index,
    kind,
    bossSeconds: 110,
    speed: Math.min(24, 17 + index * 0.45),
    approachSeconds: kind === 'bonus' ? 12 : kind === 'rush' ? 2 : 39,
  });
};
export const SKY_LEGACY_STAGES = Object.freeze([
  stage('THE VERDANT FRONTIER', 'forest', 'ANTLERED LEVIATHAN', 'leviathan', 'serpent', 0),
  stage('TEMPLE OF TIDES', 'ruins', 'THE SUNKEN ORACLE', 'sentinel', 'oracle', 1),
  stage('SPORE CATHEDRAL', 'fungi', 'TWIN CROWN HYDRA', 'leviathan', 'twin', 2),
  stage('THE IRON PASSAGE', 'tunnel', 'MANTIS WARDEN', 'titan', 'mech', 3),
  stage('THE EMERALD RESPITE', 'forest', '', '', '', 4, 'bonus'),
  stage('NAUTILUS AFTERLIGHT', 'void', 'THE LANTERN COLONY', 'sentinel', 'swarm', 5),
  stage('GLACIER OF GIANTS', 'ice', 'PRISM BROOD', 'sentinel', 'shellSwarm', 6),
  stage('THE OBSIDIAN ORACLE', 'ruins', 'STONEHEART SENTINEL', 'sentinel', 'stoneOracle', 7),
  stage('SIEGE OF THE SKYWAY', 'tunnel', 'IRON CROWN CAPTAIN', 'titan', 'mech', 8),
  stage('THE ORCHID ABYSS', 'fungi', 'BLOOM OF HUNGER', 'sentinel', 'shellSwarm', 9),
  stage('FOSSIL MOON', 'ice', 'TWIN FOSSIL WYRMS', 'leviathan', 'skeletalTwin', 10),
  stage('THE AURORA RESPITE', 'ice', '', '', '', 11, 'bonus'),
  stage('THE EIGHTFOLD ENGINE', 'citadel', 'CLOCKWORK CONSTELLATION', 'sentinel', 'swarm', 12),
  stage('CROWN OF ASH', 'tunnel', 'THE CROWNED ORACLE', 'sentinel', 'oracle', 13),
  stage('THE IVORY VOID', 'void', 'BONEWING LEVIATHAN', 'leviathan', 'skeletalSerpent', 14),
  stage('DREADNOUGHT HORIZON', 'citadel', 'THE MANTA DREADNOUGHT', 'titan', 'carrier', 15),
  stage('THE SUNKEN SUN', 'citadel', 'SOLAR SOVEREIGN', 'titan', 'solarTitan', 16),
  stage('THE LAST CONVERGENCE', 'void', 'THE CONVERGENCE', 'leviathan', 'twin', 17, 'rush'),
]);
export const SKY_FINAL_GUARDIANS = Object.freeze(
  [2, 13, 0, 1, 10, 7, 14].map((i) => SKY_LEGACY_STAGES[i]),
);
/** Preview worlds preserve the requested sequence and physical journey identity.
 * A retained, compatible guardian rig is explicitly marked as a development
 * stand-in where original artwork is not active. Prepared guardians retain their
 * original metre-scale rigs; native signature acceptance is tracked separately.
 * Cathedral uses its dedicated six-leg encounter and prepared articulated assembly. */
const previewWorld = (worldId, name, biome, legacySource, description) => ({
  worldId,
  name,
  biome,
  legacySource,
  description,
});
const authoredBriefings = Object.freeze({
  'twin-executioners': Object.freeze({
    strength: 'Linked aircraft alternate flush volleys and precision shots',
    weakness:
      'Thread their cable around a copper gantry horn, then disable both open reactors before repair',
  }),
  'scrap-emperor': Object.freeze({
    strength: 'Rebuilds cannons and ship-bow shields from real salvage piles',
    weakness:
      'Deny cannon and armor supplies, feed the red fuel tank, then attack its rebuilding reactor',
  }),
  vesper: Object.freeze({
    strength: 'Pressure rings with quiet gaps and a locked sonic pulse',
    weakness: 'Bait a pulse into the crystals, then fire at the resonator exposed by its bank',
  }),
  'hourglass-king': Object.freeze({
    strength: 'Counter-rotating crowns and a repeating time fracture',
    weakness: 'Break the three exterior clocks; their damage survives each rewind',
  }),
  'cathedral-of-thunder': Object.freeze({
    strength: 'Six articulated legs and an armoured nave',
    weakness: 'Lure lightning into grounding rods, break locked knees, then enter the nave',
  }),
  'prism-widow': Object.freeze({
    strength: 'Crystal legs and deceptive reflections',
    weakness: 'Shoot mirror bearings to reflect its beam into the abdomen; cut web anchors',
  }),
  cinderheart: Object.freeze({
    strength: 'Armoured basalt shell and eruption vents',
    weakness: 'Rupture coolant tanks to freeze one side, force a roll and attack its underside',
  }),
  'mourning-bride': Object.freeze({
    strength: 'Phantom echoes repeat your flight path',
    weakness: 'Shoot the wreck searchlight to illuminate anchor engines; route echoes into them',
  }),
  'iron-orchid': Object.freeze({
    strength: 'Magnetic pollen and homing seeds',
    weakness:
      'A/D orbit, W/S altitude: wash pollen at side waterfalls, bait seeds into roots, then climb above the bloom',
  }),
});
const authoredWorlds = [
  previewWorld(
    'cathedral-of-thunder',
    'CATHEDRAL OF THUNDER',
    'ruins',
    7,
    'Cross a flooded city through rooftop corridors and climb above its broken copper spires.',
  ),
  previewWorld(
    'prism-widow',
    'THE PRISM WIDOW',
    'ice',
    6,
    'Thread the shattered orbital telescope through mirror fields and crystal apertures.',
  ),
  previewWorld(
    'cinderheart',
    'CINDERHEART',
    'tunnel',
    3,
    'Dive between basalt ridges and coolant stations inside a volcanic caldera.',
  ),
  previewWorld(
    'mourning-bride',
    'THE MOURNING BRIDE',
    'void',
    15,
    'Navigate a moonlit airship graveyard through wreckage lanes and salvage pockets.',
  ),
  previewWorld(
    'iron-orchid',
    'THE IRON ORCHID',
    'fungi',
    9,
    'Weave through suspended gardens and irrigation corridors inside an overgrown biodome.',
  ),
  previewWorld(
    'hourglass-king',
    'THE HOURGLASS KING',
    'ruins',
    13,
    'Circle a ruined observatory through alternating routes around its luminous sandfall.',
  ),
  previewWorld(
    'scrap-emperor',
    'THE SCRAP EMPEROR',
    'citadel',
    8,
    'Collect a rich salvage chain between orbital presses, broken hulls and cutting machinery.',
  ),
  previewWorld(
    'vesper',
    'VESPER',
    'ice',
    15,
    'Ride the gaps between suspended crystal formations in a deep resonance canyon.',
  ),
  previewWorld(
    'twin-executioners',
    'THE TWIN EXECUTIONERS',
    'tunnel',
    2,
    'Cross mirrored flight lanes between moving shipyard gantries and construction frames.',
  ),
  previewWorld(
    'abyssal-angler',
    'THE ABYSSAL ANGLER',
    'void',
    14,
    'Find the open route through storm-cloud banks around an abandoned rescue station.',
  ),
  previewWorld(
    'redline-express',
    'THE REDLINE EXPRESS',
    'tunnel',
    3,
    'Follow an aerial mountain railway through tunnel mouths and unfinished bridges.',
  ),
  previewWorld(
    'dragon-sovereign',
    'THE PAPER SOVEREIGN',
    'citadel',
    0,
    'Thread stark fabrication frames through a white chamber of folded metal architecture.',
  ),
  previewWorld(
    'mother-of-mines',
    'MOTHER OF MINES',
    'fungi',
    12,
    'Follow a collection spiral through the chambers of a hollow asteroid nest.',
  ),
  previewWorld(
    'pale-surgeon',
    'THE PALE SURGEON',
    'citadel',
    1,
    'Make precise climbs and descents between the operating arms of a capital-ship repair dock.',
  ),
  previewWorld(
    'hollow-moon',
    'THE HOLLOW MOON',
    'void',
    7,
    'Skim low-orbit debris, dive through crater-shaped gates and rise over the broken rings.',
  ),
  previewWorld(
    'ember-parliament',
    'THE EMBER PARLIAMENT',
    'tunnel',
    5,
    'Race between antenna towers above a burning communications city.',
  ),
  previewWorld(
    'deep-anchor',
    'THE DEEP ANCHOR',
    'void',
    10,
    'Climb the broken space-elevator shaft through anchor frames and suspended structural masses.',
  ),
  previewWorld(
    'helios-unbound',
    'HELIOS UNBOUND',
    'citadel',
    16,
    'Sweep between solar collectors and heat-shield lanes around an artificial sun.',
  ),
  previewWorld(
    'last-reflection',
    'THE LAST REFLECTION',
    'void',
    6,
    'Break a repeating flight pattern through the final command ship as its hull opens into space.',
  ),
];
export const SKY_STAGES = Object.freeze([
  Object.freeze({
    ...SKY_LEGACY_STAGES[0],
    id: 'sky-01-dragon',
    worldId: 'verdant-frontier',
    bossName: 'THE VERDANT DRAGON',
    levelNumber: 1,
    retainedLegacy: false,
    bossPreview: false,
    description: 'Leave the forest canopy, clear the formations and confront the original dragon.',
  }),
  Object.freeze({
    ...SKY_LEGACY_STAGES[0],
    id: 'sky-02-gilded-leviathan',
    worldId: 'gilded-leviathan',
    levelNumber: 2,
    retainedLegacy: false,
    bossPreview: false,
    encounterId: 'gilded-leviathan',
    artVariant: 'gilded-leviathan',
    name: 'THE GILDED LEVIATHAN',
    biome: 'golden-industrial',
    sky: '#6f5d42',
    fog: '#bda879',
    ground: '#6b6250',
    accent: '#ffd184',
    bossName: 'THE GILDED LEVIATHAN',
    bossForm: 'gildedLeviathan',
    bossProfile: 'gildedLeviathan',
    bossDesign: null,
    approachSeconds: 39,
    description:
      'Ascend above the abandoned industrial coast, disarm the escorts and enter the whale.',
  }),
  ...authoredWorlds.map((world, index) => {
    const legacy = SKY_LEGACY_STAGES[world.legacySource];
    const authoredBoss = [
      'cathedral-of-thunder',
      'prism-widow',
      'cinderheart',
      'mourning-bride',
      'iron-orchid',
      'hourglass-king',
      'scrap-emperor',
      'vesper',
      'twin-executioners',
    ].includes(world.worldId);
    const enemyArtVariant = {
      'cathedral-of-thunder': 'enemy-gargoyle',
      'prism-widow': 'enemy-mirror-scarab',
      cinderheart: 'enemy-slag-skimmer',
      'mourning-bride': 'enemy-phantom-echo',
      'iron-orchid': 'enemy-homing-seed',
    }[world.worldId];
    const [sky, fog, ground, accent] = palettes[world.biome];
    return Object.freeze({
      ...legacy,
      ...world,
      sky,
      fog,
      ground,
      accent,
      id: `sky-${String(index + 3).padStart(2, '0')}-${world.worldId}`,
      levelNumber: index + 3,
      retainedLegacy: false,
      bossName: world.name,
      bossPreview: world.worldId !== 'cathedral-of-thunder',
      authoredBoss,
      briefing: authoredBriefings[world.worldId] ?? null,
      ...(enemyArtVariant ? { enemyArtVariant } : {}),
      signaturePreview: authoredBoss && world.worldId !== 'cathedral-of-thunder',
      ...(world.worldId === 'cathedral-of-thunder' ? { encounterId: 'cathedral-of-thunder' } : {}),
      ...(authoredBoss ? { artVariant: world.worldId } : {}),
      previewBossName: authoredBoss ? world.name : legacy.bossName,
      kind: 'combat',
      approachSeconds: 39,
      speed: Math.min(24, 18 + index * 0.3),
    });
  }),
]);

/** Select the authored encounter for a valid active run; callers never increment
 * the encounter beyond the final guardian. Invalid indices return no encounter. */
export function skyEncounter(sector, encounter = 0) {
  const current = SKY_STAGES[sector];
  return current?.kind === 'rush' ? SKY_FINAL_GUARDIANS[encounter] : current;
}

// Closed pursuit lanes in metres. The first point is the readable attack pass;
// the remaining control points carry each guardian through a close facing pass and a bounded retreat in
// depth. Original lateral curves preserve flyby banking and continuous acceleration.
const GUARDIAN_LANES = {
  prototypeDragon: {
    seconds: 14.4,
    points: [
      [3.78, 4.4, -10.22],
      [5.94, 3.9, -12.2],
      [0, 4.5, -13.96],
      [-5.94, 4.2, -11.76],
      [-4.32, 4.8, -10.0],
      [1.44, 4.6, -13.08],
    ],
  },
  stone: {
    seconds: 18,
    points: [
      [0, 5.6, -11.76],
      [4, 5.68, -14.62],
      [5, 5.6, -22.1],
      [-2, 5.68, -23.42],
      [-5, 5.6, -16.82],
      [-3, 5.68, -12.86],
    ],
  },
  serpent: {
    seconds: 14.4,
    points: [
      [3.1, 4.6, -11.32],
      [4.3, 3, -15.94],
      [0, 4, -24.74],
      [-4.2, 3.8, -19.9],
      [-3.4, 7, -12.42],
      [1.2, 7.2, -17.48],
    ],
  },
  sentinel: {
    seconds: 13.8,
    points: [
      [0, 5, -11.54],
      [4.3, 6.8, -17.7],
      [2.2, 3, -23.42],
      [-3.5, 3.4, -14.62],
      [-4, 6.8, -21.22],
      [-1, 7, -15.72],
    ],
  },
  titan: {
    seconds: 15.2,
    points: [
      [2.8, 4.5, -11.76],
      [4.2, 6, -17.04],
      [0, 7, -23.86],
      [-3.8, 5, -12.2],
      [-2, 2.8, -18.14],
      [1, 3.5, -22.76],
    ],
  },
  carrier: {
    seconds: 16.4,
    points: [
      [2.5, 5, -15.92],
      [3.3, 6.3, -22.96],
      [0, 6.6, -30.0],
      [-3.3, 4.2, -24.28],
      [-2.5, 3.8, -16.36],
      [0, 5.2, -22.52],
    ],
  },
};
export const SKY_GUARDIAN_ENTRY_SECONDS = 4.5;
export const SKY_PROTOTYPE_ROAR_AGE = 1.3;
/** The close pass sends a visible pressure wake through the rider plane. Its
 * centre follows the authored pass (stone stays grounded), never the player. */
export function skyGuardianWake(profile) {
  return { x: 0, y: profile === 'stoneOracle' ? 5.6 : 4.8, z: 1, radius: 2.2, impactAge: 2.4 };
}
const ease = (n) => {
  const t = Math.max(0, Math.min(1, n));
  return Math.max(0, Math.min(1, t * t * t * (t * (t * 6 - 15) + 10)));
};
function guardianLane(profile) {
  if (profile === 'stoneOracle') return GUARDIAN_LANES.stone;
  if (profile === 'carrier') return GUARDIAN_LANES.carrier;
  if (['mech', 'solarTitan'].includes(profile)) return GUARDIAN_LANES.titan;
  if (['oracle', 'stoneOracle', 'swarm', 'shellSwarm'].includes(profile))
    return GUARDIAN_LANES.sentinel;
  return GUARDIAN_LANES.serpent;
}
function sampleGuardianLane(lane, time) {
  const phase = ((time % lane.seconds) + lane.seconds) % lane.seconds;
  const progress = (phase * lane.points.length) / lane.seconds;
  const index = Math.floor(progress),
    u = progress - index;
  const point = (offset) => lane.points[(index + offset + lane.points.length) % lane.points.length];
  const [a, b, c, d] = [-1, 0, 1, 2].map(point);
  const position = [],
    velocity = [];
  for (let axis = 0; axis < 3; axis++) {
    // Quintic Hermite joins share position, tangent AND acceleration at each
    // knot. The old cubic spline's abrupt acceleration changes jolted banking.
    const v0 = (c[axis] - a[axis]) / 2,
      v1 = (d[axis] - b[axis]) / 2;
    const a0 = c[axis] - 2 * b[axis] + a[axis],
      a1 = d[axis] - 2 * c[axis] + b[axis];
    const p0 = b[axis],
      p1 = v0,
      p2 = a0 / 2;
    const delta = c[axis] - p0 - p1 - p2,
      speed = v1 - p1 - 2 * p2,
      acceleration = a1 - 2 * p2;
    const p3 = 10 * delta - 4 * speed + acceleration / 2,
      p4 = -15 * delta + 7 * speed - acceleration,
      p5 = 6 * delta - 3 * speed + acceleration / 2;
    position.push(p0 + p1 * u + p2 * u ** 2 + p3 * u ** 3 + p4 * u ** 4 + p5 * u ** 5);
    velocity.push(
      ((p1 + 2 * p2 * u + 3 * p3 * u ** 2 + 4 * p4 * u ** 3 + 5 * p5 * u ** 4) *
        lane.points.length) /
        lane.seconds,
    );
  }
  return { position, velocity, phase };
}
// A C1 Hermite fly-by gives a readable approach, one close lateral pass and
// retreat. End tangent exactly joins the pursuit lane; the pass never teleports.
function guardianEntry(lane, age, stone, prototype = false) {
  const endpoint = sampleGuardianLane(lane, 0);
  const heights = stone ? [5.6, 5.6, 5.6, 5.6] : [5, 6, 4.8, 6];
  const knots = [
    [0, [0, heights[0], -65]],
    [1.2, [-6, heights[1], -30]],
    [2.4, [0, heights[2], -9]],
    [3.2, [6, heights[3], -28]],
    [SKY_GUARDIAN_ENTRY_SECONDS, endpoint.position],
  ];
  if (prototype)
    knots.splice(
      1,
      3,
      [1.2, [-2, 4, -18]],
      [1.6, [0, 3.3, -6]],
      [2.15, [0, 3.3, -6]],
      [2.4, [0, 4.8, -10]],
      [3.2, [4, 5.2, -23]],
    );
  const tangent = (i) =>
    i === 0 || (prototype && (i === 2 || i === 3))
      ? [0, 0, 0]
      : i === knots.length - 1
        ? endpoint.velocity
        : knots[i + 1][1].map(
            (v, axis) => (v - knots[i - 1][1][axis]) / (knots[i + 1][0] - knots[i - 1][0]),
          );
  let index = 0;
  while (index < knots.length - 2 && age >= knots[index + 1][0]) index++;
  const [t0, p0] = knots[index],
    [t1, p1] = knots[index + 1];
  const span = t1 - t0,
    u = (age - t0) / span,
    v0 = tangent(index),
    v1 = tangent(index + 1);
  const position = [],
    velocity = [];
  for (let axis = 0; axis < 3; axis++) {
    position.push(
      (2 * u ** 3 - 3 * u * u + 1) * p0[axis] +
        (u ** 3 - 2 * u * u + u) * span * v0[axis] +
        (-2 * u ** 3 + 3 * u * u) * p1[axis] +
        (u ** 3 - u * u) * span * v1[axis],
    );
    velocity.push(
      ((6 * u * u - 6 * u) * p0[axis] +
        (3 * u * u - 4 * u + 1) * span * v0[axis] +
        (-6 * u * u + 6 * u) * p1[axis] +
        (3 * u * u - 2 * u) * span * v1[axis]) /
        span,
    );
  }
  return { position, velocity, phase: 0 };
}
// The body follows the six-second head wave with a phase delay. Translation
// supplies the larger corkscrew without stretching the imported skin weights.
function prototypeCorkscrew(sample, age) {
  const elapsed = age - SKY_GUARDIAN_ENTRY_SECONDS;
  const t = Math.max(0, Math.min(1, elapsed / 1.8));
  const weight = ease(t);
  const derivative = elapsed > 0 && elapsed < 1.8 ? (30 * t * t * (t - 1) ** 2) / 1.8 : 0;
  const frequency = Math.PI / 3,
    phase = age * frequency - 0.55;
  const offsets = [1.2 * Math.sin(phase), 0.35 * Math.cos(phase), 0.8 * Math.cos(phase)];
  const velocities = [
    1.2 * frequency * Math.cos(phase),
    -0.35 * frequency * Math.sin(phase),
    -0.8 * frequency * Math.sin(phase),
  ];
  for (let axis = 0; axis < 3; axis++) {
    sample.position[axis] += weight * offsets[axis];
    sample.velocity[axis] += derivative * offsets[axis] + weight * velocities[axis];
  }
}

/** Deterministic pose in metres/metres per second from active age. A4.5s
 * intimidating fly-by joins the closed pursuit lane continuously. The domain
 * gates combat during entry and owns its single telegraphed dodge check. Unknown
 * profiles use the serpent lane; invalid age returns the entry pose. The injected
 * first-dragon prototype uses a closer lane so its imported anatomy stays readable;
 * this is physical movement shared by collisions and rendering, never camera scaling. */
export function skyGuardianPose(profile, activeAge, artVariant, design) {
  const age = Number.isFinite(activeAge) ? Math.max(0, activeAge) : 0;
  const lane =
    artVariant === 'first-dragon' ? GUARDIAN_LANES.prototypeDragon : guardianLane(profile);
  const entering = age < SKY_GUARDIAN_ENTRY_SECONDS;
  const sample = entering
    ? guardianEntry(lane, age, profile === 'stoneOracle', artVariant === 'first-dragon')
    : sampleGuardianLane(lane, age - SKY_GUARDIAN_ENTRY_SECONDS);
  if (!entering && artVariant === 'first-dragon') prototypeCorkscrew(sample, age);
  if (!entering && artVariant !== 'first-dragon') {
    const travel = skyBossTravel(design, age - SKY_GUARDIAN_ENTRY_SECONDS);
    for (let axis = 0; axis < 3; axis++) {
      sample.position[axis] += travel.offset[axis] * (axis === 2 ? 0.25 : 1);
      sample.velocity[axis] += travel.velocity[axis] * (axis === 2 ? 0.25 : 1);
    }
  }
  if (!entering) {
    // Ease to a compact lateral orbit after the unchanged protected flyby.
    const u = Math.min(1, (age - SKY_GUARDIAN_ENTRY_SECONDS) / 2);
    const weight = 1 - 0.75 * ease(u),
      derivative = u < 1 ? -0.75 * 15 * u * u * (1 - u) ** 2 : 0;
    sample.velocity[0] = sample.velocity[0] * weight + sample.position[0] * derivative;
    sample.position[0] *= weight;
  }
  const { position, velocity } = sample;
  const clearance = skyClearBossGround(position[1], velocity[1], profile, design, artVariant);
  position[1] = clearance.y;
  velocity[1] = clearance.vy;
  const beat = skyEncounterBeat(1, age - SKY_GUARDIAN_ENTRY_SECONDS);
  const phase = entering ? 'approach' : beat.attackPhase;
  const charge = entering ? 0 : beat.charge;
  const recoil = entering
    ? 0
    : Math.min(
        1,
        Math.exp(-(((beat.local - beat.firstVolley - 0.06) / 0.13) ** 2)) +
          Math.exp(-(((beat.local - beat.secondVolley - 0.06) / 0.13) ** 2)),
      );
  const volley = entering ? 0 : beat.ordinal;
  return {
    x: position[0],
    y: position[1],
    z: position[2],
    vx: velocity[0],
    vy: velocity[1],
    vz: velocity[2],
    ...(artVariant === 'first-dragon'
      ? { entryRoar: ease((age - 1) / 0.6) * (1 - ease((age - 2.4) / 0.7)) }
      : {}),
    attackPhase: phase,
    charge,
    recoil,
    volley,
  };
}
